import React, { useEffect, PropsWithChildren } from 'react'
import { useAuth } from 'react-oidc-context'

import {
  IUniConfirm_Config,
  INavConfig_FlowPage,
  INavConfig_NestedPage,
  IDynamicSkinConfig,
  AuthUserC, 
  DealerC,
  PermissionMapC
} from  '@unikey/unikey-commons/release/comm';

import {
  useAppSelector,
  useAppDispatch,
  canI, api,
  authManager,
  SplashContainer,
  attemptRetrieveAuthUser,
  appInsights,
  devLogger,
  attemptRetrieveDealerDetails,
  attemptRetrieveAvailableSubscriptionModels,
  PartnerCustomizations,
  attemptGetDealerSkinDetails,
  finishTransitioningDealer,
  PortalContainer,
} from '../internal'

interface IProps {
  match: any,
  history: any,
  key: number,
  dealerSkinningOffered: boolean,
  renderWhenPrereqsMet: () => JSX.Element
}

const PortalPrereqContainer: React.FC<IProps> = (props) => {
  const enableLogging = true;
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const $activeDealer: DealerC = useAppSelector(state => state.portal.activeDealer);
  const $transitioningDealers: boolean = useAppSelector(state => state.portal.transitioningDealers);
  const $activeDealerId: string = useAppSelector(state => state.portal.activeDealer?.id); // needs to be here
  const $authenticatedUser: AuthUserC = useAppSelector(state => state.authenticatedUser?.currentUser);
  const $permissions: PermissionMapC = useAppSelector(state => state.authenticatedUser.currentUser?.permissions);
  const $subscriptionRetrieved: boolean = useAppSelector(state => !!state.subscriptionInfo.defaultType);

  // dealerSkin
  const $dealerSkinningOffered: boolean = props.dealerSkinningOffered ?? false;
  const $dealerSkinOverrides: IDynamicSkinConfig = useAppSelector(state => state.dealerSkin.skin);
  const $dealerSkinLoading: boolean = useAppSelector(state => state.dealerSkin.loading ?? false);

  useEffect(() => {
    if (auth.isAuthenticated) {
      _getAuthUser();
    }
  }, [auth.isLoading, auth.isAuthenticated])

  useEffect(() => {
    if ($activeDealerId) {
      _loadDealerReqs()
    }
  }, [$activeDealerId])

  const _loadDealerReqs = async () => {
    const dealerId = api.getDealerId();
    await dispatch(attemptRetrieveDealerDetails(dealerId));  

    if ($dealerSkinningOffered) {
      await dispatch(attemptGetDealerSkinDetails());
    }
    // we need to have fetched the subscriptions before we can reliably show anything
    if (!$subscriptionRetrieved) {
      await dispatch(attemptRetrieveAvailableSubscriptionModels())
    }
    dispatch(finishTransitioningDealer(dealerId))
  }

  const _getAuthUser = async () => {
    // If OIDC says we're authenticated but the API token sink is empty (a store
    // divergence or partial teardown), rehydrate it from the OIDC user before
    // fetching the auth user — otherwise getAuthUser 401s and bounces to login,
    // and the splash hangs. This is the root fix for the "user not found" hang.
    await authManager.reconcile();
    await dispatch(attemptRetrieveAuthUser());
  }

  if (enableLogging) devLogger(`----`);

  // boolean to determine if we have everything needed to proceed loading the /portal pages
  let pendingPrerequisiteData = false;
  // first auth user data,
  if (!$authenticatedUser) {
    pendingPrerequisiteData = true;
    devLogger('prereq not met: auth user');
  }
  // we must have an active dealer set before we can render anything
  if (!$activeDealerId) {
    pendingPrerequisiteData = true;
    devLogger('prereq not met: active dealer id');
  }
  // make sure we have a dealer otherwise, should go to 
  if (!$authenticatedUser?.getDealer?.()?.id) {
    pendingPrerequisiteData = true;
    devLogger('prereq not met: user dealer id');
  }
  // second dealer skin (if required)
  if ($dealerSkinningOffered && $dealerSkinLoading) {
    devLogger('prereq not met: dealer skin');
    pendingPrerequisiteData = true;
  }
  // if we're transitioning between different dealers
  if ($transitioningDealers) {
    devLogger('prereq not met: transitioning dealers');
    pendingPrerequisiteData = true;
  }

  // if we haven't retrieved the subscription info yet, we can't show anything
  if (!$subscriptionRetrieved) {
    devLogger('prereq not met: dealer subscription info');
    pendingPrerequisiteData = true;
  }

  if (!pendingPrerequisiteData) {
    if (enableLogging) devLogger(`##################\n###### NOT SPLASHING #######\ncan view admin portal\n############################`);
  }

  const allow = (!pendingPrerequisiteData);
  return allow
    ? (<PortalContainer />)
    : (<SplashContainer fromComponentName="portalPrereq" />)
}

export default PartnerCustomizations(PortalPrereqContainer, {
  componentName: 'PortalPrereq'
})